/**
 * Client-side projectile system for auto-cannon tracers.
 * Pure logic module — no React, no Rapier, no zustand.
 * Module-level state read directly by R3F components via import.
 *
 * Tracers are simple ballistic projectiles: spawn at muzzle, fly straight,
 * despawn on lifetime or collision. No physics bodies — just math.
 *
 * Collision uses swept segment-circle intersection to prevent tunneling
 * (tracers move ~3.3 units/frame at 200 u/s, asteroids have radius 1.5-3).
 */

import {
  CANNON_SPEED,
  CANNON_LIFETIME,
  CANNON_SPREAD_RAD,
  CANNON_MUZZLE_OFFSET,
  CANNON_FIRE_INTERVAL_TICKS,
  MAX_TRACERS,
  WORLD_HALF_SIZE,
  PLANET_RADIUS,
} from '@warmancer/shared';

export interface Tracer {
  active: boolean;
  x: number;
  y: number;
  prevX: number;
  prevY: number;
  vx: number;
  vy: number;
  age: number;
  ownerEntityId: number;
}

export interface Impact {
  x: number;
  y: number;
  age: number;
}

const IMPACT_LIFETIME = 0.15;
const MAX_IMPACTS = 20;

// Module-level state
const tracers: Tracer[] = [];
const impacts: Impact[] = [];
const fireRateCooldowns: Map<number, number> = new Map(); // entityId → last fire tick

// Pre-allocate tracer pool
for (let i = 0; i < MAX_TRACERS; i++) {
  tracers.push({
    active: false, x: 0, y: 0, prevX: 0, prevY: 0,
    vx: 0, vy: 0, age: 0, ownerEntityId: 0,
  });
}

// Pre-allocate impact pool
for (let i = 0; i < MAX_IMPACTS; i++) {
  impacts.push({ x: 0, y: 0, age: IMPACT_LIFETIME + 1 });
}

/**
 * Try to spawn a tracer for a given ship.
 * Respects fire rate cooldown per entity.
 */
export function trySpawn(
  shipX: number,
  shipY: number,
  rotation: number,
  shipVx: number,
  shipVy: number,
  entityId: number,
  currentTick: number,
): void {
  // Check fire rate cooldown
  const lastFire = fireRateCooldowns.get(entityId) ?? -CANNON_FIRE_INTERVAL_TICKS;
  if (currentTick - lastFire < CANNON_FIRE_INTERVAL_TICKS) return;

  // Find inactive tracer slot
  let slot: Tracer | null = null;
  for (let i = 0; i < tracers.length; i++) {
    if (!tracers[i].active) {
      slot = tracers[i];
      break;
    }
  }
  if (!slot) return; // Pool full

  // Random spread
  const spread = (Math.random() - 0.5) * 2 * CANNON_SPREAD_RAD;
  const fireAngle = rotation + spread;

  // Muzzle position
  const cosA = Math.cos(rotation);
  const sinA = Math.sin(rotation);
  const muzzleX = shipX + cosA * CANNON_MUZZLE_OFFSET;
  const muzzleY = shipY + sinA * CANNON_MUZZLE_OFFSET;

  // Tracer velocity = cannon speed in fire direction + ship velocity (Galilean)
  const cosF = Math.cos(fireAngle);
  const sinF = Math.sin(fireAngle);
  const tracerVx = cosF * CANNON_SPEED + shipVx;
  const tracerVy = sinF * CANNON_SPEED + shipVy;

  slot.active = true;
  slot.x = muzzleX;
  slot.y = muzzleY;
  slot.prevX = muzzleX;
  slot.prevY = muzzleY;
  slot.vx = tracerVx;
  slot.vy = tracerVy;
  slot.age = 0;
  slot.ownerEntityId = entityId;

  fireRateCooldowns.set(entityId, currentTick);
}

/**
 * Advance all active tracers by dt seconds. Deactivate expired ones.
 * Saves previous position for swept collision detection.
 */
export function update(dt: number): void {
  for (let i = 0; i < tracers.length; i++) {
    const t = tracers[i];
    if (!t.active) continue;

    // Save previous position for swept collision
    t.prevX = t.x;
    t.prevY = t.y;

    t.x += t.vx * dt;
    t.y += t.vy * dt;
    t.age += dt;

    if (t.age >= CANNON_LIFETIME) {
      t.active = false;
    }
  }

  // Age impacts
  for (let i = 0; i < impacts.length; i++) {
    impacts[i].age += dt;
  }
}

/**
 * Segment-circle intersection test.
 * Checks if the line segment from (ax,ay) to (bx,by) intersects a circle at (cx,cy) with radius r.
 * Returns the intersection point closest to A, or null if no intersection.
 */
function segmentCircleIntersect(
  ax: number, ay: number,
  bx: number, by: number,
  cx: number, cy: number,
  r: number,
): { x: number; y: number } | null {
  const dx = bx - ax;
  const dy = by - ay;
  const fx = ax - cx;
  const fy = ay - cy;

  const a = dx * dx + dy * dy;
  if (a < 1e-8) {
    // Degenerate segment (zero length) — point-in-circle test
    if (fx * fx + fy * fy < r * r) return { x: ax, y: ay };
    return null;
  }

  const b = 2 * (fx * dx + fy * dy);
  const c = fx * fx + fy * fy - r * r;

  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0) return null;

  const sqrtD = Math.sqrt(discriminant);
  const t1 = (-b - sqrtD) / (2 * a);
  const t2 = (-b + sqrtD) / (2 * a);

  // Check t1 first (closer intersection)
  if (t1 >= 0 && t1 <= 1) {
    return { x: ax + t1 * dx, y: ay + t1 * dy };
  }
  // Check t2 (farther intersection, but segment endpoint might be inside circle)
  if (t2 >= 0 && t2 <= 1) {
    return { x: ax + t2 * dx, y: ay + t2 * dy };
  }
  // Check if segment is entirely inside the circle (both t values outside [0,1] but surrounding)
  if (t1 < 0 && t2 > 1) {
    return { x: ax, y: ay };
  }

  return null;
}

/**
 * Check tracer collisions against game entities.
 * Uses swept segment-circle intersection (prevPos → currentPos) to prevent tunneling.
 */
export function checkCollisions(
  asteroids: ReadonlyArray<{ x: number; y: number; radius: number }>,
  ships: ReadonlyArray<{ entityId: number; x: number; y: number; radius: number }>,
  moonX: number,
  moonY: number,
  moonRadius: number,
): void {
  for (let i = 0; i < tracers.length; i++) {
    const t = tracers[i];
    if (!t.active) continue;

    // Check boundary walls (simple point check — boundaries are axis-aligned)
    if (
      t.x < -WORLD_HALF_SIZE || t.x > WORLD_HALF_SIZE ||
      t.y < -WORLD_HALF_SIZE || t.y > WORLD_HALF_SIZE
    ) {
      // Clamp impact position to boundary
      const ix = Math.max(-WORLD_HALF_SIZE, Math.min(WORLD_HALF_SIZE, t.x));
      const iy = Math.max(-WORLD_HALF_SIZE, Math.min(WORLD_HALF_SIZE, t.y));
      spawnImpact(ix, iy);
      t.active = false;
      continue;
    }

    // Check planet (always at origin) — swept
    const planetHit = segmentCircleIntersect(
      t.prevX, t.prevY, t.x, t.y, 0, 0, PLANET_RADIUS,
    );
    if (planetHit) {
      spawnImpact(planetHit.x, planetHit.y);
      t.active = false;
      continue;
    }

    // Check moon — swept
    const moonHit = segmentCircleIntersect(
      t.prevX, t.prevY, t.x, t.y, moonX, moonY, moonRadius,
    );
    if (moonHit) {
      spawnImpact(moonHit.x, moonHit.y);
      t.active = false;
      continue;
    }

    // Check asteroids — swept
    let hit = false;
    for (let j = 0; j < asteroids.length; j++) {
      const a = asteroids[j];
      const aHit = segmentCircleIntersect(
        t.prevX, t.prevY, t.x, t.y, a.x, a.y, a.radius,
      );
      if (aHit) {
        spawnImpact(aHit.x, aHit.y);
        t.active = false;
        hit = true;
        break;
      }
    }
    if (hit) continue;

    // Check ships (skip owner) — swept
    for (let j = 0; j < ships.length; j++) {
      const s = ships[j];
      if (s.entityId === t.ownerEntityId) continue;
      const sHit = segmentCircleIntersect(
        t.prevX, t.prevY, t.x, t.y, s.x, s.y, s.radius,
      );
      if (sHit) {
        spawnImpact(sHit.x, sHit.y);
        t.active = false;
        break;
      }
    }
  }
}

function spawnImpact(x: number, y: number): void {
  // Find oldest or expired impact slot
  let oldest = 0;
  let oldestAge = -1;
  for (let i = 0; i < impacts.length; i++) {
    if (impacts[i].age > IMPACT_LIFETIME) {
      oldest = i;
      break;
    }
    if (impacts[i].age > oldestAge) {
      oldestAge = impacts[i].age;
      oldest = i;
    }
  }
  impacts[oldest].x = x;
  impacts[oldest].y = y;
  impacts[oldest].age = 0;
}

/** Get all tracers (active and inactive — caller filters by .active). */
export function getTracers(): readonly Tracer[] {
  return tracers;
}

/** Get all impacts (caller filters by age < IMPACT_LIFETIME). */
export function getImpacts(): readonly Impact[] {
  return impacts;
}

/** Get impact lifetime constant for rendering. */
export function getImpactLifetime(): number {
  return IMPACT_LIFETIME;
}

/** Reset all state. Called on game cleanup. */
export function reset(): void {
  for (let i = 0; i < tracers.length; i++) {
    tracers[i].active = false;
  }
  for (let i = 0; i < impacts.length; i++) {
    impacts[i].age = IMPACT_LIFETIME + 1;
  }
  fireRateCooldowns.clear();
}
